import React, { useCallback, useEffect } from 'react';
import { LayoutContent, LayoutContentCenter, LayoutControls, ContentFrame, Button, CircularFrame, Typography, LayoutContentBody, LayoutContentHeader, } from '@pinwheel/origami';
import { useSelector } from 'react-redux';
import useLinkNavigate from '../../hooks/useLinkNavigate';
import { useAppDispatch, useAppSelector } from '../../state';
import { selectMatchedPlatforms, setUsingMultiSupplierEligibility, } from '../../state/slices/slice-link-api';
import { setSelectedPlatform } from '../../state/slices/slice-provider-entity';
import { useGetLocalizedString } from '../../hooks/useGetLocalizedString';
import { EventSurface } from '../../types/events';
import { updateEventSource } from '../../state/slices/slice-segment';
import eventQueue from '../../events/queue';
import { selectExperiments } from '../../state/slices/slice-link-params';
import { cleanPartnerBasedPlatformNames } from '../../utils/partner-based-string-cleaning';
import { showBottomSheet } from '../../state/slices/slice-bottom-sheet';
import { ScreenTitleHeader } from '../../components/ScreenTitleHeader';
export function getTitle(getLocalizedString, platformName) {
    if (platformName === 'ADP') {
        return getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_TITLE_ADP', [
            platformName,
        ]);
    }
    return getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_TITLE', [
        platformName,
    ]);
}
export function getSubtitle(getLocalizedString, platformName, employerName) {
    return employerName
        ? getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_SUBTITLE_EMPLOYER', [
            platformName,
            employerName,
        ])
        : getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_SUBTITLE_NO_EMPLOYER', [
            platformName,
        ]);
}
export default function SingleResult() {
    var matchedPlatforms = useAppSelector(selectMatchedPlatforms);
    var dispatch = useAppDispatch();
    var navigate = useLinkNavigate();
    var getLocalizedString = useGetLocalizedString();
    var onClickHandler = useCallback(function () {
        var matchedPlatform = matchedPlatforms[0];
        dispatch(setSelectedPlatform(matchedPlatform.platform));
        dispatch(setUsingMultiSupplierEligibility(true));
        navigate('/link-api/login');
    }, [dispatch, matchedPlatforms, navigate]);
    var experiments = useSelector(selectExperiments);
    var _a = matchedPlatforms[0], platform = _a.platform, employerName = _a.employerName;
    var platformName = cleanPartnerBasedPlatformNames(platform.name);
    useEffect(function () {
        if (platform.key === 'adpPortal' ||
            platform.key === 'adpPartnershipFallbackIntegrationMarch2024') {
            var eventId = 'adpRerouteBottomSheet';
            eventQueue.emit('bottom_sheet', { eventId: eventId, isOpen: true });
            dispatch(showBottomSheet({
                title: getLocalizedString('PAYROLL_ACCOUNT_FOUND'),
                body: getLocalizedString('WE_FOUND_AN_ACCOUNT', [platformName]),
                dismissButtonText: getLocalizedString('CONTINUE_SEARCHING'),
                dismissButtonVariant: 'ghost',
                navigateButtonText: getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_CONTINUE', [platformName]),
                navigateButtonVariant: 'solid',
                navigateButtonAction: 'loginWithADPTestPlatform',
                imageId: 'BigStatusIcon-check',
                eventId: eventId,
            }));
            navigate('/search/search');
        }
        else if (experiments.skipEligibilityCheckSingleResult) {
            onClickHandler();
        }
        else {
            dispatch(updateEventSource(EventSurface.PLATFORM_ELIGIBILITY_LOOKUP_RESULTS));
            eventQueue.emit('impression', {});
        }
    }, [
        dispatch,
        getLocalizedString,
        navigate,
        platform,
        platformName,
        onClickHandler,
        experiments.skipEligibilityCheckSingleResult,
    ]);
    return (React.createElement(ContentFrame, { "data-testid": "mse-singleResult" },
        React.createElement(LayoutContent, null,
            React.createElement(LayoutContentCenter, null,
                React.createElement(LayoutContentHeader, null,
                    React.createElement(CircularFrame, { size: "lg" },
                        React.createElement("img", { src: platform.logoUrl, alt: "Icon ".concat(platform.name) }))),
                React.createElement(LayoutContentBody, null,
                    React.createElement(ScreenTitleHeader, { variant: "header", as: "h2", align: "center", weight: "bold" }, getTitle(getLocalizedString, platformName)),
                    React.createElement(Typography, { variant: "caption", color: "light", weight: "normal", align: "center" },
                        React.createElement("span", { 
                            // This string may contain a bolded segment using HTML tags.
                            // Since the content that we're using here comes from our own
                            // codebase, it's safe to use this directive.
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML: {
                                __html: getSubtitle(getLocalizedString, platformName, employerName),
                            } }))))),
        React.createElement(LayoutControls, null,
            React.createElement(Button, { eventId: "MSE-selectProvider", onClick: onClickHandler }, getLocalizedString('CONTINUE')),
            React.createElement(Button, { "data-testid": "MSE-goToSearch", eventId: "MSE-goToSearch", variant: "ghost", onClick: function () { return navigate('/search/search'); } }, getLocalizedString('MS_ELIGIBILITY_SINGLE_ACCOUNT_SKIP')))));
}
